import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    MenuItem,
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio,
    Checkbox,
    Typography,
    IconButton,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';

const EditQuestionFormModal = ({ isOpen, onClose, onSubmit, editingQuestion }) => {
    const [formData, setFormData] = useState({
        exam_id: '',
        subject_id: '',
        topic_id: '',
        question_text: '',
        question_type: 'MCQ_RADIO',
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        correct_option: '',
        correct_option_array: [],
        marks: 1,
        question_image: null,
        option_a_image: null,
        option_b_image: null,
        option_c_image: null,
        option_d_image: null,
    });

    const [error, setError] = useState('');

    useEffect(() => {
        if (editingQuestion) {
            setFormData({
                ...editingQuestion,
                correct_option_array: editingQuestion.correct_option_array || [],
                correct_option: editingQuestion.correct_option || '',
                question_image: null,
                option_a_image: null,
                option_b_image: null,
                option_c_image: null,
                option_d_image: null,
            });
        }
    }, [editingQuestion]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => {
            const newArray = checked
                ? [...prev.correct_option_array, name]
                : prev.correct_option_array.filter((opt) => opt !== name);
            return {
                ...prev,
                correct_option_array: newArray,
            };
        });
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: files[0],
        }));
    };

    const handleSubmit = () => {
        if (!formData.question_text || !formData.option_a || !formData.option_b || !formData.marks) {
            setError("Please fill all required fields.");
            return;
        }

        setError('');
        onSubmit(formData);
    };

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Edit Question
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error">{error}</Alert>}

                <TextField
                    label="Question"
                    name="question_text"
                    fullWidth
                    margin="normal"
                    value={formData.question_text}
                    onChange={handleInputChange}
                />

                <TextField
                    label="Marks"
                    name="marks"
                    type="number"
                    fullWidth
                    margin="normal"
                    value={formData.marks}
                    onChange={handleInputChange}
                />

                <FormControl fullWidth margin="normal">
                    <TextField
                        select
                        label="Question Type"
                        name="question_type"
                        value={formData.question_type}
                        onChange={handleInputChange}
                    >
                        <MenuItem value="MCQ_RADIO">MCQ (Single Answer)</MenuItem>
                        <MenuItem value="MCQ_CHECKBOX">MCQ (Multiple Answers)</MenuItem>
                    </TextField>
                </FormControl>

                <TextField
                    label="Option A"
                    name="option_a"
                    fullWidth
                    margin="normal"
                    value={formData.option_a}
                    onChange={handleInputChange}
                />
                <input type="file" name="option_a_image" onChange={handleFileChange} />

                <TextField
                    label="Option B"
                    name="option_b"
                    fullWidth
                    margin="normal"
                    value={formData.option_b}
                    onChange={handleInputChange}
                />
                <input type="file" name="option_b_image" onChange={handleFileChange} />

                <TextField
                    label="Option C"
                    name="option_c"
                    fullWidth
                    margin="normal"
                    value={formData.option_c}
                    onChange={handleInputChange}
                />
                <input type="file" name="option_c_image" onChange={handleFileChange} />

                <TextField
                    label="Option D"
                    name="option_d"
                    fullWidth
                    margin="normal"
                    value={formData.option_d}
                    onChange={handleInputChange}
                />
                <input type="file" name="option_d_image" onChange={handleFileChange} />

                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    Upload Question Image (optional)
                </Typography>
                <input type="file" name="question_image" onChange={handleFileChange} />

                {formData.question_type === 'MCQ_RADIO' ? (
                    <FormControl component="fieldset" sx={{ mt: 2 }}>
                        <Typography>Correct Option</Typography>
                        <RadioGroup
                            row
                            name="correct_option"
                            value={formData.correct_option}
                            onChange={handleInputChange}
                        >
                            <FormControlLabel value="A" control={<Radio />} label="A" />
                            <FormControlLabel value="B" control={<Radio />} label="B" />
                            <FormControlLabel value="C" control={<Radio />} label="C" />
                            <FormControlLabel value="D" control={<Radio />} label="D" />
                        </RadioGroup>
                    </FormControl>
                ) : (
                    <FormControl component="fieldset" sx={{ mt: 2 }}>
                        <Typography>Correct Options</Typography>
                        <FormControlLabel
                            control={<Checkbox checked={formData.correct_option_array.includes("A")} onChange={handleCheckboxChange} name="A" />}
                            label="A"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={formData.correct_option_array.includes("B")} onChange={handleCheckboxChange} name="B" />}
                            label="B"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={formData.correct_option_array.includes("C")} onChange={handleCheckboxChange} name="C" />}
                            label="C"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={formData.correct_option_array.includes("D")} onChange={handleCheckboxChange} name="D" />}
                            label="D"
                        />
                    </FormControl>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <motion.div whileTap={{ scale: 0.95 }}>
                    <Button variant="contained" onClick={handleSubmit} color="primary">
                        Update
                    </Button>
                </motion.div>
            </DialogActions>
        </Dialog>
    );
};

export default EditQuestionFormModal;
